import { useStore } from 'hooks/useStore';
import {
  RoomBpmByRunType,
  RoomBpmByRunTypePayload
} from 'Interfaces/ReferenceInterfaces/RoomBpmByRunTypeInterface';
import { useEffect, useRef, useState } from 'react';
import { DataTableStateEvent } from 'primereact/datatable';
import { useInterval } from 'hooks/usePolling';
import { getJitteredInterval } from 'utils/pollingUtility';
import * as XLSX from 'xlsx';
import {toast} from 'react-toastify';

interface FormData {
  runTypeId: number;
  bpm: number;
  roomTypeId: number;
}

export const useRoomBpmByRunTypeTable = (pageSize: number = 10) => {
  const { roomBpmByRunTypeStore, roomStore, runTypeStore } = useStore();

  const [selectedRow, setSelectedRow] = useState<RoomBpmByRunType | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemMenu = useRef<any>(null);
  const environment = import.meta.env.VITE_ENVIRONMENT;
  const POLLING_INTERVAL = getJitteredInterval();

  // Load initial data when component mounts
  useEffect(() => {
    roomBpmByRunTypeStore.fetchRoomBpmByRunTypes(currentPage, pageSize);
    roomStore.fetchRooms();
    roomStore.fetchRoomTypes();

    // Fetch run types if not already loaded
    if (runTypeStore.runTypes.length === 0) {
      runTypeStore.fetchRunTypes();
    }
  }, [currentPage, pageSize]);

  // Set up polling for data updates
  useInterval(async () => {
    try {
      await roomBpmByRunTypeStore.pollRoomBpmByRunTypes(currentPage, pageSize);
      await roomStore.pollRoomTypes();
    } catch (error) {
      // Don't break the polling cycle on error
      if (environment === 'development') {
        console.error('Error during polling:', error);
      }
      toast.error('Error during polling for Room BPM by Run Type data!');
    }
  }, POLLING_INTERVAL);

  // Handle page change
  const onPageChange = (event: DataTableStateEvent) => {
    if (event.page !== undefined) {
      const newPage = event.page + 1; // PrimeReact uses 0-based indexing, our API uses 1-based
      setCurrentPage(newPage);
      roomBpmByRunTypeStore.fetchRoomBpmByRunTypes(newPage, pageSize);
    }
  };

  // Prepare room options from roomStore
  const roomOptions = roomStore.rooms.map(room => ({
    label: room.room_short ? `${room.room_short} - ${room.room_long}` : room.room_long || '',
    value: room.id
  }));

  // Prepare room type options from roomStore
  const roomTypeOptions = roomStore.roomTypeOptions;

  // Prepare run type options from runTypeStore
  const runTypeOptions = runTypeStore.runTypes.map(runType => ({
    label: runType.description || '',
    value: runType.id
  }));

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const handleAddRow = async (formData: FormData): Promise<void> => {
    try {
      setIsSubmitting(true);
      if (selectedRow) {
        // Edit existing row
        const updatePayload: RoomBpmByRunTypePayload = {
          room_type: formData.roomTypeId,
          job_type: formData.runTypeId,
          bpm: formData.bpm,
          mo_hc: null,
          pa_hc: null
        };

        const success = await roomBpmByRunTypeStore.updateRoomBpmByRunType(
          selectedRow.id,
          updatePayload
        );
        if (!success) {
          throw new Error('Failed to update room BPM by run type');
        }
      } else {
        // Add new row
        const createPayload: RoomBpmByRunTypePayload = {
          room_type: formData.roomTypeId,
          job_type: formData.runTypeId,
          bpm: formData.bpm,
          mo_hc: null,
          pa_hc: null,
          flag: true
        };

        const success = await roomBpmByRunTypeStore.createRoomBpmByRunType(createPayload);
        if (!success) {
          throw new Error('Failed to create room BPM by run type');
        }
      }
      setSelectedRow(null);
      // Refresh data while maintaining current page
      await roomBpmByRunTypeStore.fetchRoomBpmByRunTypes(currentPage, pageSize);
    } catch (error) {
      throw error; // Re-throw the error so the modal can handle it
    } finally {
      setIsSubmitting(false);
    }
  };

  const [isRowDeleting, setIsRowDeleting] = useState<boolean>(false);
  const handleDeleteRow = async () => {
    try {
      setIsRowDeleting(true);
      if (selectedRow) {
        await roomBpmByRunTypeStore.deleteRoomBpmByRunType(selectedRow.id);
        setSelectedRow(null);
        setShowDeleteModal(false);
        // Refresh data while maintaining current page
        await roomBpmByRunTypeStore.fetchRoomBpmByRunTypes(currentPage, pageSize);
      }
    } catch (error) {
      if (environment === 'development') {
        console.error("Error during deletion:", error);
      }
      toast.error('Error during deletion for Room BPM by Run Type data!');
    } finally {
      setIsRowDeleting(false);
    }
  };

  const prepareFormData = (row: RoomBpmByRunType): FormData => {
    return {
      runTypeId: row.job_type.id,
      roomTypeId: row.room_type.id,
      bpm: row.bpm || 0
    };
  };

  // Method to manually refresh data
  const refreshData = async () => {
    await roomBpmByRunTypeStore.refreshData(currentPage, pageSize);
  };

  const handleExportToExcel = () => {
    const exportData = roomBpmByRunTypeStore.roomBpmByRunTypes.map(item => ({
      'Room Type': item.room_type.description,
      'Job Type': item.job_type.description,
      'BPM': item.bpm || '-'
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Room BPM by Run Type');
    XLSX.writeFile(workbook, 'room_bpm_by_run_type_export.xlsx');
  };

  return {
    roomBpmByRunTypeData: roomBpmByRunTypeStore.roomBpmByRunTypes,
    isLoading:
      roomBpmByRunTypeStore.isLoading ||
      roomStore.isLoading ||
      roomStore.roomTypesLoading ||
      runTypeStore.isLoading,
    pagination: roomBpmByRunTypeStore.pagination,
    itemMenu,
    selectedRow,
    showAddModal,
    showDeleteModal,
    setSelectedRow,
    setShowAddModal,
    setShowDeleteModal,
    handleAddRow,
    handleDeleteRow,
    prepareFormData,
    roomOptions,
    runTypeOptions,
    onPageChange,
    currentPage,
    roomTypeOptions,
    isRowDeleting,
    isSubmitting,
    refreshData,
    handleExportToExcel
  };
};
